/* eslint-disable */
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Sankey, Rectangle, Layer } from "recharts";

// ---------------------------------------------------------------------------
// Help bubble: "?" icon with hover tooltip
// ---------------------------------------------------------------------------
function HelpBubble({ text, align }) {
  const [show, setShow] = useState(false);
  // align: "left" = tooltip opens to the right, "right" = opens to the left, default = centered
  const posStyle =
    align === "left"
      ? { left: 0, transform: "none" }
      : align === "right"
        ? { right: 0, transform: "none" }
        : { left: "50%", transform: "translateX(-50%)" };
  return (
    <span
      style={{ position: "relative", display: "inline-flex", cursor: "help" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: "1px solid #bbb",
          fontSize: 9,
          fontWeight: 700,
          color: "#aaa",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          textTransform: "none",
          letterSpacing: 0,
        }}
      >
        ?
      </span>
      {show && (
        <div
          style={{
            position: "absolute",
            top: 20,
            ...posStyle,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 6,
            padding: "8px 10px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            fontSize: 11,
            fontWeight: 400,
            color: "#555",
            width: 240,
            whiteSpace: "normal",
            textTransform: "none",
            letterSpacing: "normal",
            lineHeight: 1.5,
            zIndex: 1000,
          }}
        >
          {text}
        </div>
      )}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Color logic
// ---------------------------------------------------------------------------
function getNodeFill(payload, clientColor) {
  if (payload.highlight) return "#D32F2F";
  return clientColor || "#1565C0";
}

// ---------------------------------------------------------------------------
// Custom node renderer – grouped nodes get italic text + alpha bar + member tooltip
// ---------------------------------------------------------------------------
function SankeyNode({
  x,
  y,
  width,
  height,
  index,
  payload,
  clientColor,
  onNodeHover,
  onNodeInfoHover,
  nodeMetricsMap,
}) {
  const fill = getNodeFill(payload, clientColor);
  const isGrouped = payload._grouped || payload.members?.length > 0;
  const members = payload.members || [];
  const textColor = payload.highlight ? "#D32F2F" : isGrouped ? "#888" : "#444";

  const handleEnter = () => {
    if (members.length > 0 && onNodeHover) onNodeHover(members);
    if (onNodeInfoHover) {
      const nm = (nodeMetricsMap || {})[payload.name] || {};
      onNodeInfoHover({ name: payload.name, metrics: nm });
    }
  };
  const handleLeave = () => {
    if (onNodeHover) onNodeHover(null);
    if (onNodeInfoHover) onNodeInfoHover(null);
  };

  return (
    <Layer key={`node-${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        fillOpacity={isGrouped ? 0.4 : 1}
        rx={2}
        stroke="none"
      />
      {/* Invisible hit area for node hover (covers bar + label) */}
      <rect
        x={x - 2}
        y={y}
        width={width + 4 + 120}
        height={height}
        fill="transparent"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ cursor: members.length > 0 ? "help" : "default" }}
      />
      <text
        x={x + width + 6}
        y={y + height / 2}
        textAnchor="start"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={payload.highlight ? 600 : 500}
        fontStyle={isGrouped ? "italic" : "normal"}
        fill={textColor}
        style={{ pointerEvents: "none" }}
      >
        {payload.name}
      </text>
    </Layer>
  );
}

// ---------------------------------------------------------------------------
// Custom link renderer – hover changes fill to clientColor
// ---------------------------------------------------------------------------
// Link type colors: populated from backend's transfers_link_types config
// Passed as prop linkTypeColors = { key: color }


function SankeyLink({
  sourceX,
  targetX,
  sourceY,
  targetY,
  sourceControlX,
  targetControlX,
  linkWidth,
  index,
  payload,
  clientColor,
  onLinkHover,
  linkTypeColors,
}) {
  const linkType = payload?.link_type;
  const typeColor = (linkTypeColors || {})[linkType];

  const pathD = `
    M${sourceX},${sourceY + linkWidth / 2}
    C${sourceControlX},${sourceY + linkWidth / 2}
     ${targetControlX},${targetY + linkWidth / 2}
     ${targetX},${targetY + linkWidth / 2}
    L${targetX},${targetY - linkWidth / 2}
    C${targetControlX},${targetY - linkWidth / 2}
     ${sourceControlX},${sourceY - linkWidth / 2}
     ${sourceX},${sourceY - linkWidth / 2}
    Z
  `;

  const [hovered, setHovered] = useState(false);
  const baseFill = typeColor || "#d5d5d5";
  const hoverFill = typeColor || clientColor || "#1976d2";

  return (
    <Layer key={`link-${index}`}>
      <path
        d={pathD}
        fill={hovered ? hoverFill : baseFill}
        fillOpacity={hovered ? (typeColor ? 0.5 : 0.3) : (typeColor ? 0.25 : 0.6)}
        stroke="none"
        style={{
          transition: "fill 0.15s, fill-opacity 0.15s",
          cursor: "pointer",
        }}
        onMouseEnter={() => {
          setHovered(true);
          if (onLinkHover) {
            const m = payload?._m || {};
            const src = payload?.sourceName || "?";
            const tgt = payload?.targetName || "?";
            onLinkHover({ src, tgt, metrics: m });
          }
        }}
        onMouseLeave={() => {
          setHovered(false);
          if (onLinkHover) onLinkHover(null);
        }}
      />
    </Layer>
  );
}

// ---------------------------------------------------------------------------
// Number formatting for info panels
// ---------------------------------------------------------------------------
function fmtNumber(val) {
  if (val == null || val === 0) return "0";
  if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
  return Number(val).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function LogisticsSankeyChart({
  data,
  layers,
  layerTooltips,
  unitOptions,
  clientColor,
  linkTypeColors,
  groupedLabel,
}) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 500 });
  const [hoveredMembers, setHoveredMembers] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);   // {src, tgt, metrics}
  const [hoveredNode, setHoveredNode] = useState(null);   // {name, metrics}

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setSize({ w: width, h: height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Pre-compute per-node metrics (Recharts does not pass custom node properties to tooltip)
  const nodeMetrics = useMemo(() => {
    if (!data?.nodes || !data?.links) return {};
    const nm = {};
    data.nodes.forEach((n) => {
      nm[n.name] = {};
    });
    data.links.forEach((l) => {
      const srcName = data.nodes[l.source]?.name;
      const tgtName = data.nodes[l.target]?.name;
      Object.entries(l.metrics || {}).forEach(([k, v]) => {
        if (srcName) nm[srcName][k] = (nm[srcName][k] || 0) + (v || 0);
        if (tgtName) nm[tgtName][k] = (nm[tgtName][k] || 0) + (v || 0);
      });
    });
    return nm;
  }, [data]);

  const layerLabels = layers || [];
  const headerHeight = 32;
  const chartHeight = size.h - headerHeight;
  const margin = { top: 8, right: 160, bottom: 8, left: 50 };

  const usableWidth = size.w - margin.left - margin.right;
  const nodeWidth = 12;
  const colCount = layerLabels.length || 1;
  const colGap = (usableWidth - nodeWidth) / Math.max(colCount - 1, 1);
  const colPositions = layerLabels.map(
    (_, i) => margin.left + i * colGap + nodeWidth / 2,
  );

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Layer headers with optional tooltips */}
      <div style={{ position: "relative", height: headerHeight, zIndex: 10 }}>
        {layerLabels.map((label, i) => {
          const tip = (layerTooltips || [])[i] || "";
          return (
            <span
              key={i}
              style={{
                position: "absolute",
                left: colPositions[i],
                transform: "translateX(-50%)",
                top: 8,
                fontSize: 11,
                fontWeight: 600,
                color: "#999",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {label}
              {tip && (
                <HelpBubble
                  text={tip}
                  align={
                    i === 0
                      ? "left"
                      : i === layerLabels.length - 1
                        ? "right"
                        : undefined
                  }
                />
              )}
            </span>
          );
        })}
      </div>
      {/* Sankey */}
      {data && data.nodes && data.nodes.length > 0 && chartHeight > 100 && (
        <Sankey
          data={data}
          width={size.w}
          height={chartHeight}
          nodeWidth={nodeWidth}
          nodePadding={12}
          margin={margin}
          align="justify"
          link={<SankeyLink clientColor={clientColor} onLinkHover={setHoveredLink} linkTypeColors={linkTypeColors} />}
          node={
            <SankeyNode
              clientColor={clientColor}
              onNodeHover={setHoveredMembers}
              onNodeInfoHover={setHoveredNode}
              nodeMetricsMap={nodeMetrics}
            />
          }
        />
      )}
      {/* Fixed info panel - top right: metrics + grouped members (stacked) */}
      {(hoveredLink || hoveredNode || (hoveredMembers && hoveredMembers.length > 0)) && (
        <div
          style={{
            position: "absolute",
            top: headerHeight + 8,
            right: 12,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            zIndex: 20,
            pointerEvents: "none",
            maxWidth: 240,
            opacity: 0.88,
            transition: "opacity 0.15s ease-in",
          }}
        >
          {/* Metrics panel */}
          {(hoveredLink || hoveredNode) && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                padding: "6px 10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: 11,
                color: "#444",
                lineHeight: 1.7,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                {hoveredLink
                  ? `${hoveredLink.src} \u2192 ${hoveredLink.tgt}`
                  : hoveredNode?.name || ""}
              </div>
              {(unitOptions || []).map((u) => (
                <div key={u.meta_name} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span style={{ color: "#888" }}>{u.name}</span>
                  <span style={{ fontWeight: 500 }}>
                    {fmtNumber((hoveredLink || hoveredNode)?.metrics?.[u.meta_name])}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Grouped members panel */}
          {hoveredMembers && hoveredMembers.length > 0 && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                padding: "6px 10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: 10,
                color: "#666",
                lineHeight: 1.6,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: "#aaa",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 2,
                }}
              >
                {hoveredMembers.length} {groupedLabel || "grouped elements"}
              </div>
              {hoveredMembers.map((m, i) => (
                <div key={i}>{m}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

LogisticsSankeyChart.propTypes = {
  data: PropTypes.object,
  layers: PropTypes.array,
  layerTooltips: PropTypes.array,
  unitOptions: PropTypes.array,
  clientColor: PropTypes.string,
  linkTypeColors: PropTypes.object,
  groupedLabel: PropTypes.string,
};
